import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowClockwise, ArrowLeft, CheckCircle, Info, Package, Pause, WarningCircle } from 'shared/icons';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import ConfirmModal from '../../ui/ConfirmModal';
import { Mono } from '../../ui/Mono';
import { useToast } from '../../ui/Toast';
import ScanInput from '../collector/ScanInput';
import { PlayScanError, PlayScanSuccess } from '../collector/collectorFeedback';
import { ExtractListRows } from '../payload';
import {
    AppointKitByInventoryId,
    ClaimBenchMachine,
    FinishProductionOrder,
    GetProductionOrder,
    ListBenchMachines,
    ListProductionOrders,
    OpenProductionOrder,
    PauseProductionOrder,
    ReleaseBenchMachine,
    ScanProductionOutputLpn,
} from './productionApi';
import { GetBenchTerminalId } from './benchTerminal';
import { FormatLabel } from './productionLabels';
import {
    BenchOrderState,
    BenchProgress,
    DescribeBenchExit,
    DescribeLpnScan,
    DescribePutaway,
    IsLpnCode,
    IsOpenAtMachine,
    ProductionOrderStatusVariant,
    PutawayWaitsLogistics,
    SortBenchOrders,
} from './productionModel';

// A BANCADA DA FÁBRICA (frente 3) — desktop 1280×1024, SEM lotes. O operador:
//   1. escolhe a máquina e a OP (a OP define o produto de saída e o kit; o
//      inventory_id sai do anúncio do Full desse produto);
//   2. bipa o PALLET de saída: uma etiqueta de LPN em branco (a bancada não
//      cria nem imprime pallet). Bipar outro pallet no meio da OP encerra o
//      atual (ele vai para a logística por OS) e o novo nasce ali;
//   3. bipa a etiqueta do Full de cada kit pronto (o inventory_id, ex.:
//      SITE31754) — CONFERÊNCIA + apontamento automático
//      (+1 kit no pallet). Bateu, soma; não bateu, a tela grita e não escreve.
// Referência de UX: o packing do checkout (bipar, feedback grande, seguir).
const MODE_PICK_MACHINE = 'PICK_MACHINE';
const MODE_PICK_ORDER = 'PICK_ORDER';
const MODE_BENCH = 'BENCH';

// O que a bancada lista: liberadas (abrem) e planejadas (só aparecem, esperam
// o supervisor). Encerradas não aparecem.
const BENCH_LIST_STATUSES = ['RUNNING', 'PLANNED'];

// De quanto em quanto tempo a bancada confere se a OP continua aberta aqui (o
// gestor pode finalizar ou cancelar, o supervisor pode tirar da bancada).
const OPEN_ORDER_CHECK_MS = 15000;

// A OP saiu desta bancada sem o operador pedir: o backend responde isto em
// apontar, pausar e finalizar (com o estado real nos detalhes).
const LEFT_BENCH_CODE = 'WMS_PRODUCTION_ORDER_LEFT_BENCH';

// Uma máquina, um operador, um terminal: a bancada renova o sinal de que está
// na máquina (sem sinal por 2 min, ela fica livre para outro terminal).
const MACHINE_HEARTBEAT_MS = 30000;
// A máquina não está mais com este terminal (outro assumiu, ou ficou sem sinal).
const MACHINE_LOST_CODES = ['WMS_MACHINE_IN_USE', 'WMS_MACHINE_NOT_HELD'];

const ProductionMlbBench = ({ label, operator, onExit }) => {
    const toast = useToast();
    const operatorCode = operator?.employee_code || undefined;
    const terminalId = useMemo(() => GetBenchTerminalId(), []);

    const [mode, setMode] = useState(MODE_PICK_MACHINE);
    const [machines, setMachines] = useState(null);
    const [machine, setMachine] = useState(null);
    const [orders, setOrders] = useState(null);
    const [order, setOrder] = useState(null);

    // O pallet de saída que o operador bipou (com `output_state`: na saída ou
    // em formação). `needLpn`: ao abrir a OP, o 1º bipe tem de ser o pallet.
    const [outputLpn, setOutputLpn] = useState(null);
    const [kitsInLpn, setKitsInLpn] = useState(0);
    const [needLpn, setNeedLpn] = useState(true);
    // O que o último bipe de pallet fez (pallet anterior para a logística,
    // onde o novo ficou) — linhas curtas acima do bipe.
    const [lpnNotice, setLpnNotice] = useState([]);
    const [scanError, setScanError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [kitsDone, setKitsDone] = useState(0);
    const [confirmFinish, setConfirmFinish] = useState(false);
    // A TELA PADRÃO de mensagem (OP finalizada, pausada, meta atingida ou saiu
    // da bancada): toma a tela até o operador voltar às OPs.
    const [notice, setNotice] = useState(null);

    // A máquina que ESTE terminal assumiu. Sair da bancada por qualquer caminho
    // (sair, deslogar no cabeçalho, crachá expirado, navegar para fora) a
    // LIBERA — e o backend pausa a OP aberta nela: fora da máquina a OP fica
    // livre para outra continuar.
    const claimedRef = useRef(null);
    useEffect(
        () => () => {
            const claimed = claimedRef.current;
            if (claimed) {
                claimedRef.current = null;
                ReleaseBenchMachine(claimed.code, {
                    operator_code: claimed.operatorCode,
                    terminal_id: claimed.terminalId,
                }).catch(() => {});
            }
        },
        [],
    );

    // O corpo de toda ação da bancada: crachá, máquina e terminal.
    const benchBody = (extra = {}) => ({
        operator_code: operatorCode,
        machine_code: machine?.code,
        terminal_id: terminalId,
        ...extra,
    });

    // ── Carregamentos ────────────────────────────────────────────────────────
    // As máquinas e quem está em cada uma (livre, este terminal, outro).
    const loadMachines = useCallback(async () => {
        try {
            const data = await ListBenchMachines({ terminal_id: terminalId, operator_code: operatorCode });
            setMachines(ExtractListRows(data, ['results']));
        } catch {
            setMachines([]);
        }
    }, [terminalId, operatorCode]);

    useEffect(() => {
        loadMachines();
    }, [loadMachines]);

    // A OP não tem máquina: a bancada vê TODAS as liberadas e planejadas e
    // mostra, em cada uma, se abre aqui, se está rodando em outra máquina ou se
    // espera o supervisor.
    const loadOrders = useCallback(async (machineRow) => {
        setOrders(null);
        const responses = await Promise.all(
            BENCH_LIST_STATUSES.map((status) => ListProductionOrders({ status, limit: 200 }).catch(() => null)),
        );
        const rows = responses.flatMap((data) => ExtractListRows(data, ['production_orders']));
        setOrders(SortBenchOrders(rows, machineRow));
    }, []);

    // Volta para a lista (depois de pausar, finalizar ou trocar de OP).
    const backToOrders = useCallback(() => {
        setOrder(null);
        setOutputLpn(null);
        setKitsInLpn(0);
        setNeedLpn(true);
        setLpnNotice([]);
        setScanError(null);
        setKitsDone(0);
        setMode(MODE_PICK_ORDER);
        loadOrders(machine);
    }, [loadOrders, machine]);

    // Mostra a tela padrão. A OP não está mais nesta bancada: sair daqui não
    // pausa nada.
    const showNotice = useCallback((next) => {
        setConfirmFinish(false);
        setScanError(null);
        setNotice(next);
    }, []);

    // Assumir a máquina (crachá + terminal): outro terminal nela = recusa.
    const pickMachine = async (row) => {
        setBusy(true);
        try {
            await ClaimBenchMachine(row.code, { operator_code: operatorCode, terminal_id: terminalId });
            claimedRef.current = { code: row.code, operatorCode, terminalId };
            setMachine(row);
            loadOrders(row);
            setMode(MODE_PICK_ORDER);
        } catch (error) {
            toast.error(error?.message || label('wms.common.load_error'));
            loadMachines();
        } finally {
            setBusy(false);
        }
    };

    // Trocar de máquina (ou perdê-la): solta a que estava com este terminal.
    const backToMachines = async () => {
        const claimed = claimedRef.current;
        claimedRef.current = null;
        if (claimed) {
            await ReleaseBenchMachine(claimed.code, {
                operator_code: claimed.operatorCode,
                terminal_id: claimed.terminalId,
            }).catch(() => {});
        }
        setMachine(null);
        setOrders(null);
        setOrder(null);
        // A lista de antes está velha (quem está em cada máquina mudou): só
        // mostra depois de reler.
        setMachines(null);
        setMode(MODE_PICK_MACHINE);
        loadMachines();
    };

    // A máquina não está mais com este terminal: diz e volta a escolher.
    const showMachineLost = useCallback(() => {
        claimedRef.current = null;
        showNotice({
            tone: 'warning',
            action: 'machines',
            title: FormatLabel(label('wms.bench.notice_machine_title'), { machine: machine?.code ?? '' }),
            text: label('wms.bench.notice_machine_text'),
        });
    }, [machine, label, showNotice]);

    // Renova o sinal de que este terminal está na máquina.
    useEffect(() => {
        if (!machine?.code || mode === MODE_PICK_MACHINE || notice?.action === 'machines') {
            return undefined;
        }
        const timer = window.setInterval(() => {
            ClaimBenchMachine(machine.code, { operator_code: operatorCode, terminal_id: terminalId }).catch((error) => {
                if (MACHINE_LOST_CODES.includes(error?.code)) {
                    showMachineLost();
                }
            });
        }, MACHINE_HEARTBEAT_MS);
        return () => window.clearInterval(timer);
    }, [machine, mode, notice, operatorCode, terminalId, showMachineLost]);

    const dismissNotice = () => {
        const action = notice?.action;
        setNotice(null);
        if (action === 'machines') {
            backToMachines();
        } else {
            backToOrders();
        }
    };

    // A OP saiu desta bancada (gestor finalizou/cancelou, supervisor tirou,
    // outra máquina abriu): relê a OP e diz o que aconteceu.
    const showLeftBench = useCallback(
        async (fallbackText) => {
            if (!order) {
                return;
            }
            try {
                const detail = await GetProductionOrder(order.id);
                showNotice(DescribeBenchExit(detail, label, FormatLabel));
            } catch {
                showNotice({
                    tone: 'warning',
                    title: FormatLabel(label('wms.bench.notice_left_title'), { code: order.code }),
                    text: fallbackText || '',
                });
            }
        },
        [order, label, showNotice],
    );

    // Confere de tempos em tempos se a OP continua aberta AQUI — sem isso o
    // operador só descobre no próximo bipe que o supervisor a tirou.
    useEffect(() => {
        if (mode !== MODE_BENCH || !order?.id || !machine?.id || busy || notice || confirmFinish) {
            return undefined;
        }
        const timer = window.setInterval(() => {
            GetProductionOrder(order.id)
                .then((detail) => {
                    if (detail && !IsOpenAtMachine(detail, machine)) {
                        showNotice(DescribeBenchExit(detail, label, FormatLabel));
                    }
                })
                .catch(() => {});
        }, OPEN_ORDER_CHECK_MS);
        return () => window.clearInterval(timer);
    }, [mode, order, machine, busy, notice, confirmFinish, label, showNotice]);

    // Abrir = prender a OP nesta máquina (a que estava aberta aqui é pausada).
    // O 1º bipe depois de abrir é o PALLET de saída.
    const pickOrder = async (row) => {
        if (!BenchOrderState(row, machine).canOpen) {
            return;
        }
        setBusy(true);
        setScanError(null);
        try {
            const detail = await OpenProductionOrder(row.id, benchBody());
            setOrder(detail);
            setKitsDone(Number(detail?.kits_done ?? 0));
            setOutputLpn(null);
            setKitsInLpn(0);
            setNeedLpn(true);
            setLpnNotice([]);
            setMode(MODE_BENCH);
        } catch (error) {
            if (MACHINE_LOST_CODES.includes(error?.code)) {
                showMachineLost();
                return;
            }
            // A saída da máquina tem outro pallet: a OP não começa até a
            // logística retirá-lo. O detalhe (qual saída, qual pallet) vai miúdo.
            if (error?.code === 'WMS_PRODUCTION_OUTPUT_OCCUPIED') {
                showNotice({
                    tone: 'warning',
                    title: label('wms.bench.notice_output_title'),
                    text: error.message,
                    detail: FormatLabel(label('wms.bench.notice_output_detail'), {
                        location: error.details?.location_code ?? '—',
                        lpn: error.details?.lpn_code ?? '—',
                    }),
                });
                return;
            }
            toast.error(error?.message || label('wms.common.load_error'));
            // A lista estava velha (outra bancada abriu, o gestor mexeu): recarrega.
            loadOrders(machine);
        } finally {
            setBusy(false);
        }
    };

    // A tela da OP que terminou (finalizar ou meta): kits, e — se algum pallet
    // foi para a logística — "aguarde a coleta" e a OS de cada um.
    const finishedNotice = (titleKey, kits, putaway) => ({
        tone: 'success',
        title: FormatLabel(label(titleKey), { code: order?.code ?? '' }),
        text: [
            FormatLabel(label('wms.bench.notice_kits_text'), { kits }),
            PutawayWaitsLogistics(putaway) ? label('wms.bench.notice_wait_logistics') : null,
        ]
            .filter(Boolean)
            .join(' '),
        detail: DescribePutaway(putaway, label, FormatLabel).join(' '),
    });

    // Erro de bipe comum às duas chamadas (pallet e kit).
    const handleScanFailure = async (error, read, kind) => {
        PlayScanError();
        if (error?.code === LEFT_BENCH_CODE) {
            await showLeftBench(error?.message);
            return;
        }
        if (MACHINE_LOST_CODES.includes(error?.code)) {
            showMachineLost();
            return;
        }
        if (error?.code === 'WMS_PRODUCTION_SCAN_LPN_FIRST') {
            setNeedLpn(true);
        }
        setScanError({
            kind,
            message: error?.message || label('wms.common.load_error'),
            read: error?.details?.read_inventory_id ?? read,
            code: error?.code ?? null,
        });
    };

    // ── O bipe do PALLET (LPN em branco) ────────────────────────────────────
    const handleLpnScan = async (rawCode, method) => {
        try {
            const result = await ScanProductionOutputLpn(
                order.id,
                benchBody({ scan_method: method, lpn_code: rawCode.trim().toUpperCase() }),
            );
            PlayScanSuccess();
            setOutputLpn({ ...(result?.lpn ?? {}), output_state: result?.output_state ?? null });
            setKitsInLpn(Number(result?.kits_in_lpn ?? 0));
            setNeedLpn(false);
            setLpnNotice(DescribeLpnScan(result, label, FormatLabel));
        } catch (error) {
            await handleScanFailure(error, rawCode, 'lpn');
        }
    };

    // ── O bipe da etiqueta do Full ──────────────────────────────────────────
    // O backend confere contra os inventory_id do produto de saída (anúncios do
    // Full); o esperado NUNCA aparece aqui (regra do dono — o operador bipa e o
    // sistema diz certo/errado).
    const handleKitScan = async (rawCode, method) => {
        try {
            const result = await AppointKitByInventoryId(
                order.id,
                benchBody({ scan_method: method, inventory_id: rawCode }),
            );
            PlayScanSuccess();
            setKitsDone(Number(result?.kits_done ?? kitsDone + 1));
            // Meta de kits alcançada: a OP foi finalizada pelo próprio bipe.
            if (result?.production_order_finished) {
                showNotice(
                    finishedNotice('wms.bench.notice_target_title', Number(result?.kits_done ?? 0), result?.putaway),
                );
                return;
            }
            setOutputLpn((current) => ({
                ...(current ?? {}),
                ...(result?.lpn ?? {}),
                output_state: result?.output_state ?? current?.output_state ?? null,
            }));
            setKitsInLpn(Number(result?.kits_in_lpn ?? kitsInLpn + 1));
        } catch (error) {
            await handleScanFailure(error, rawCode, 'kit');
        }
    };

    // Um campo de bipe só: código de LPN (PREFIXO-AAMMDD-SEQ) é o pallet; o
    // resto é a etiqueta do Full do kit. Enquanto o pallet não foi bipado, o
    // kit é recusado aqui mesmo (o 1º bipe é o pallet).
    const handleScan = async (rawCode, method) => {
        if (!order || busy) {
            return;
        }
        setScanError(null);
        if (!IsLpnCode(rawCode) && needLpn) {
            PlayScanError();
            setScanError({ kind: 'need_lpn', message: label('wms.bench.scan_lpn_first'), read: rawCode });
            return;
        }
        setBusy(true);
        try {
            if (IsLpnCode(rawCode)) {
                await handleLpnScan(rawCode, method);
            } else {
                await handleKitScan(rawCode, method);
            }
        } finally {
            setBusy(false);
        }
    };

    // PAUSAR: a OP sai desta bancada e fica livre para qualquer outra. O motivo
    // entra no histórico da OP: 'pause' (Pausar), 'switch_order' (Trocar de OP)
    // ou 'exit' (Sair da bancada — sair nunca deixa a OP presa nesta máquina).
    // Se a OP JÁ saiu da bancada (finalizada pelo gestor, tirada pelo
    // supervisor), trocar de OP e sair seguem normalmente — nunca prendem o
    // operador; Pausar mostra o que aconteceu.
    const pauseOrder = async ({ reason = 'pause', then = backToOrders } = {}) => {
        if (!order) {
            then();
            return;
        }
        setBusy(true);
        try {
            await PauseProductionOrder(order.id, benchBody({ reason }));
            if (reason === 'pause') {
                showNotice({
                    tone: 'info',
                    title: FormatLabel(label('wms.bench.notice_paused_title'), { code: order.code }),
                    text: label('wms.bench.notice_paused_text'),
                });
            } else {
                then();
            }
        } catch (error) {
            if (MACHINE_LOST_CODES.includes(error?.code)) {
                if (reason === 'exit') {
                    then();
                } else {
                    showMachineLost();
                }
            } else if (error?.code !== LEFT_BENCH_CODE) {
                toast.error(error?.message || label('wms.common.load_error'));
            } else if (reason === 'pause') {
                await showLeftBench(error?.message);
            } else {
                then();
            }
        } finally {
            setBusy(false);
        }
    };

    const finishOrder = async () => {
        if (!order) {
            return;
        }
        setBusy(true);
        try {
            const finished = await FinishProductionOrder(order.id, benchBody());
            showNotice(finishedNotice('wms.bench.notice_finished_title', kitsDone, finished?.putaway));
        } catch (error) {
            if (error?.code === LEFT_BENCH_CODE) {
                await showLeftBench(error?.message);
            } else if (MACHINE_LOST_CODES.includes(error?.code)) {
                showMachineLost();
            } else {
                toast.error(error?.message || label('wms.common.load_error'));
            }
        } finally {
            setBusy(false);
        }
    };

    const totalUnits = useMemo(() => {
        const items = outputLpn?.items ?? [];
        return items.reduce((sum, item) => sum + Number(item.qty ?? 0), 0);
    }, [outputLpn]);

    const progress = BenchProgress(kitsDone, order?.planned_kits);

    // ── A tela padrão de mensagem ────────────────────────────────────────────
    if (notice) {
        return (
            <BenchNotice
                notice={notice}
                actionLabel={label(notice.action === 'machines' ? 'wms.bench.notice_pick_machine' : 'wms.bench.notice_back')}
                onAction={dismissNotice}
            />
        );
    }

    // ── Passo 1: máquina ─────────────────────────────────────────────────────
    if (mode === MODE_PICK_MACHINE) {
        return (
            <BenchCard title={label('wms.bench.pick_machine')} onExit={onExit} exitLabel={label('wms.bench.exit')}>
                {machines === null ? (
                    <p className="text-center text-mono-500">…</p>
                ) : machines.length === 0 ? (
                    <p className="rounded-lg bg-warning-soft px-4 py-3 text-warning-text">{label('wms.bench.no_machines')}</p>
                ) : (
                    <div className="grid grid-cols-2 gap-3">
                        {/* Uma máquina, um operador: a que está com outro terminal
                            aparece com o nome de quem está nela e não abre. */}
                        {machines.map((row) => {
                            const taken = row.holder_state === 'taken';
                            return (
                                <button
                                    key={row.id}
                                    type="button"
                                    disabled={busy || taken}
                                    onClick={() => pickMachine(row)}
                                    className="rounded-2xl border-2 border-mono-300 bg-white px-5 py-6 text-left transition-colors hover:border-black disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-mono-300 dark:border-mono-700 dark:bg-mono-950 dark:hover:border-white"
                                >
                                    <Mono className="block text-2xl font-black text-black dark:text-white">{row.code}</Mono>
                                    <span className="text-sm text-mono-500">{row.sector}</span>
                                    {taken ? (
                                        <span className="mt-2 block">
                                            <Badge variant="warning">
                                                {FormatLabel(label('wms.bench.machine_taken'), {
                                                    name: row.holder?.operator_name ?? row.holder?.operator_code ?? '—',
                                                })}
                                            </Badge>
                                        </span>
                                    ) : row.holder_state === 'here' || row.holder_state === 'mine_elsewhere' ? (
                                        <span className="mt-2 block">
                                            <Badge variant="success">
                                                {label(
                                                    row.holder_state === 'here'
                                                        ? 'wms.bench.machine_here'
                                                        : 'wms.bench.machine_mine_elsewhere',
                                                )}
                                            </Badge>
                                        </span>
                                    ) : null}
                                </button>
                            );
                        })}
                    </div>
                )}
            </BenchCard>
        );
    }

    // ── Passo 2: OP ──────────────────────────────────────────────────────────
    if (mode === MODE_PICK_ORDER) {
        return (
            <BenchCard
                title={label('wms.bench.pick_order')}
                subtitle={machine?.code}
                onBack={backToMachines}
                backLabel={label('wms.bench.change_machine')}
                onExit={onExit}
                exitLabel={label('wms.bench.exit')}
            >
                {orders === null ? (
                    <p className="text-center text-mono-500">…</p>
                ) : orders.length === 0 ? (
                    <p className="rounded-lg bg-warning-soft px-4 py-3 text-warning-text">{label('wms.bench.no_orders')}</p>
                ) : (
                    <div className="grid grid-cols-2 gap-3">
                        {orders.map((row) => {
                            const state = BenchOrderState(row, machine);
                            return (
                                <button
                                    key={row.id}
                                    type="button"
                                    disabled={busy || !state.canOpen}
                                    onClick={() => pickOrder(row)}
                                    title={state.canOpen ? undefined : label(`wms.bench.order_state_${state.key}`)}
                                    className="rounded-2xl border-2 border-mono-300 bg-white px-5 py-5 text-left transition-colors hover:border-black disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-mono-300 dark:border-mono-700 dark:bg-mono-950 dark:hover:border-white"
                                >
                                    <Mono className="block text-2xl font-black text-black dark:text-white">{row.code}</Mono>
                                    <span className="mt-1 block truncate text-base text-mono-600 dark:text-mono-400">
                                        {row.product_name || row.sku || ''}
                                    </span>
                                    {row.sku_in ? (
                                        <span className="mt-1 block truncate text-sm text-mono-500 dark:text-mono-400">
                                            {label('wms.field.product_in')}: <Mono>{row.sku_in}</Mono>
                                        </span>
                                    ) : null}
                                    <span className="mt-2 flex flex-wrap items-center gap-2">
                                        <Badge variant={state.variant ?? ProductionOrderStatusVariant(row.status)}>
                                            {FormatLabel(label(`wms.bench.order_state_${state.key}`), {
                                                machine: row.machine_code ?? '',
                                            })}
                                        </Badge>
                                        {row.planned_kits ? (
                                            <Badge variant="default">
                                                {FormatLabel(label('wms.bench.kits_target'), { planned: row.planned_kits })}
                                            </Badge>
                                        ) : null}
                                        {row.kit_size ? (
                                            <Badge variant="info">{`${label('wms.field.kit_size')}: ${row.kit_size}`}</Badge>
                                        ) : null}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </BenchCard>
        );
    }

    // ── Passo 3: A BANCADA (bipar o pallet e os kits) — duas colunas 1280×1024 ─
    const errorTitleKey =
        scanError?.kind === 'need_lpn'
            ? 'wms.bench.scan_lpn_first_title'
            : scanError?.code === 'WMS_PRODUCTION_INVENTORY_ID_MISMATCH'
              ? 'wms.bench.inventory_id_mismatch'
              : scanError?.kind === 'lpn'
                ? 'wms.bench.lpn_refused'
                : 'wms.bench.scan_refused';
    const forming = outputLpn?.output_state === 'forming';
    return (
        <div className="grid grid-cols-[1.1fr_0.9fr] gap-4">
            {/* Coluna ESQUERDA — a OP e o bipe. */}
            <section className="space-y-4">
                <header className="flex items-start justify-between gap-3 rounded-2xl border-2 border-mono-300 bg-white p-5 dark:border-mono-700 dark:bg-mono-950">
                    <div className="min-w-0">
                        <Mono className="block text-3xl font-black text-black dark:text-white">{order?.code}</Mono>
                        <span className="mt-1 block truncate text-lg text-mono-600 dark:text-mono-400">
                            {order?.product_name || order?.sku || ''}
                        </span>
                        {order?.sku_in ? (
                            <span className="mt-1 block truncate text-sm text-mono-500 dark:text-mono-400">
                                {label('wms.field.product_in')}: <Mono>{order.sku_in}</Mono>
                            </span>
                        ) : null}
                    </div>
                    <div className="shrink-0 space-y-1 text-right">
                        <Mono className="block text-base text-black dark:text-white">{machine?.code}</Mono>
                        <Badge variant="info">{`${label('wms.field.kit_size')}: ${order?.kit_size ?? 1}`}</Badge>
                    </div>
                </header>

                {/* O BIPE. Um campo só: o LPN é o pallet, o resto é o kit. O 1º
                    bipe é o pallet. Regra do dono: o inventory_id esperado NUNCA
                    aparece. */}
                {order ? (
                    <div className="rounded-2xl border-2 border-black bg-white p-5 dark:border-white dark:bg-mono-950">
                        <p className="mb-1 text-lg font-bold text-black dark:text-white">
                            {label(needLpn ? 'wms.bench.scan_lpn' : 'wms.bench.scan_inventory_id')}
                        </p>
                        <p className="mb-3 text-sm text-mono-600 dark:text-mono-400">
                            {label(needLpn ? 'wms.bench.scan_lpn_help' : 'wms.bench.scan_other_lpn_help')}
                        </p>
                        <ScanInput
                            label={label}
                            onScan={handleScan}
                            disabled={busy}
                            focusKey={`bipe-${kitsInLpn}-${outputLpn?.code ?? ''}`}
                        />
                        {lpnNotice.length > 0 && !scanError ? (
                            <div className="mt-3 space-y-1 rounded-xl border-2 border-info-500 bg-info-soft p-4 text-info-text dark:bg-info-900/30 dark:text-info-200">
                                {lpnNotice.map((line) => (
                                    <p key={line} className="flex items-start gap-2 text-sm font-semibold">
                                        <Info size={18} className="mt-0.5 shrink-0" />
                                        {line}
                                    </p>
                                ))}
                            </div>
                        ) : null}
                        {scanError ? (
                            <div className="mt-3 space-y-1 rounded-xl border-2 border-danger-500 bg-danger-soft p-4 text-danger-text dark:bg-danger-900/40 dark:text-danger-200">
                                <p className="flex items-center gap-2 text-lg font-black">
                                    <WarningCircle size={24} className="shrink-0" />
                                    {label(errorTitleKey)}
                                </p>
                                <p className="text-sm">
                                    {label('wms.bench.read')}: <Mono>{scanError.read || '—'}</Mono>
                                </p>
                                {scanError.message ? <p className="text-sm">{scanError.message}</p> : null}
                            </div>
                        ) : null}
                    </div>
                ) : null}

                <div className="flex flex-wrap gap-2">
                    {/* Pausar = azul; Finalizar = verde. */}
                    <Button variant="info" size="lg" onClick={() => pauseOrder({ reason: 'pause' })} disabled={busy}>
                        <Pause size={20} className="mr-2" />
                        {label('wms.bench.pause_order')}
                    </Button>
                    <Button variant="success" size="lg" onClick={() => setConfirmFinish(true)} disabled={busy}>
                        <CheckCircle size={20} className="mr-2" />
                        {label('wms.bench.finish_order')}
                    </Button>
                    {/* Trocar de OP = pausar esta (fica livre) e escolher outra. */}
                    <Button
                        variant="ghost"
                        size="lg"
                        onClick={() => pauseOrder({ reason: 'switch_order' })}
                        disabled={busy}
                    >
                        {label('wms.bench.change_order')}
                    </Button>
                    {/* Sair = pausar (a OP fica livre para outra máquina) e só então sair. */}
                    <Button
                        variant="ghost"
                        size="lg"
                        onClick={() => pauseOrder({ reason: 'exit', then: onExit })}
                        disabled={busy}
                    >
                        <ArrowLeft size={20} className="mr-2" />
                        {label('wms.bench.exit')}
                    </Button>
                </div>
            </section>

            <ConfirmModal
                isOpen={confirmFinish}
                onClose={() => setConfirmFinish(false)}
                onConfirm={finishOrder}
                loading={busy}
                title={label('wms.bench.finish_order')}
                message={FormatLabel(label('wms.bench.finish_order_confirm'), { code: order?.code ?? '' })}
                confirmText={label('wms.bench.finish_order')}
                confirmVariant="success"
            />

            {/* Coluna DIREITA — o andamento da OP e o pallet de saída. Sem lista
                de bipes: numa OP de dias seriam milhares. */}
            <section className="space-y-4">
                <div className={`grid gap-3 ${progress.planned ? 'grid-cols-3' : 'grid-cols-1'}`}>
                    <BenchCounter title={label('wms.bench.counter_done')} value={progress.done} />
                    {progress.planned ? (
                        <>
                            <BenchCounter
                                title={label('wms.bench.counter_remaining')}
                                value={progress.remaining}
                                hint={FormatLabel(label('wms.bench.counter_target'), { planned: progress.planned })}
                            />
                            <BenchCounter title={label('wms.bench.counter_progress')} value={`${progress.percent}%`} />
                        </>
                    ) : null}
                </div>

                <div className="rounded-2xl border-2 border-mono-300 bg-white p-5 dark:border-mono-700 dark:bg-mono-950">
                    <div className="flex items-center gap-2">
                        <Package size={22} className="text-black dark:text-white" />
                        <p className="text-xs font-semibold uppercase tracking-wide text-mono-500">
                            {label('wms.bench.output_lpn')}
                        </p>
                    </div>
                    {outputLpn?.code ? (
                        <>
                            <Mono className="mt-1 block break-all text-3xl font-black text-black dark:text-white">
                                {outputLpn.code}
                            </Mono>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                <Badge variant={forming ? 'warning' : 'success'}>
                                    {forming
                                        ? label('wms.bench.lpn_state_forming')
                                        : FormatLabel(label('wms.bench.lpn_state_at_output'), {
                                              location: outputLpn.location?.code ?? '—',
                                          })}
                                </Badge>
                                <Badge variant="default">{`${kitsInLpn} ${label('wms.bench.kits')}`}</Badge>
                                <Badge variant="default">{`${totalUnits} un`}</Badge>
                            </div>
                        </>
                    ) : (
                        <p className="mt-2 text-base text-mono-600 dark:text-mono-400">{label('wms.bench.lpn_none')}</p>
                    )}
                </div>
            </section>
        </div>
    );
};

// Um contador grande da bancada (kits apontados, faltam, progresso).
const BenchCounter = ({ title, value, hint }) => (
    <div className="rounded-2xl border-2 border-mono-300 bg-white p-4 text-center dark:border-mono-700 dark:bg-mono-950">
        <p className="text-xs font-semibold uppercase tracking-wide text-mono-500">{title}</p>
        <p className="mt-1 text-4xl font-black tabular-nums text-black dark:text-white">{value}</p>
        {hint ? <p className="mt-1 text-xs text-mono-500">{hint}</p> : null}
    </div>
);

// A TELA PADRÃO de mensagem da bancada: ícone e cor pelo tom, título grande,
// o que aconteceu e um botão só. Sem campo de bipe: nada prende o foco aqui.
const NOTICE_TONES = {
    success: {
        box: 'border-success-500 bg-success-soft dark:bg-success-900/30',
        text: 'text-success-text dark:text-success-200',
        Icon: CheckCircle,
    },
    info: {
        box: 'border-info-500 bg-info-soft dark:bg-info-900/30',
        text: 'text-info-text dark:text-info-200',
        Icon: Pause,
    },
    warning: {
        box: 'border-warning-500 bg-warning-soft dark:bg-warning-900/30',
        text: 'text-warning-text dark:text-warning-200',
        Icon: WarningCircle,
    },
};

const BenchNotice = ({ notice, actionLabel, onAction }) => {
    const tone = NOTICE_TONES[notice.tone] ?? NOTICE_TONES.warning;
    const { Icon } = tone;
    return (
        <div className="mx-auto mt-6 max-w-2xl">
            <div className={`space-y-5 rounded-2xl border-2 p-10 text-center ${tone.box}`} role="status">
                <Icon size={64} className={`mx-auto ${tone.text}`} />
                <h2 className={`text-3xl font-black ${tone.text}`}>{notice.title}</h2>
                {notice.text ? <p className={`text-lg ${tone.text}`}>{notice.text}</p> : null}
                {/* O detalhe miúdo (qual saída, qual pallet, qual OS). */}
                {notice.detail ? <p className={`text-sm opacity-80 ${tone.text}`}>{notice.detail}</p> : null}
                <Button variant="primary" size="lg" className="mx-auto" onClick={onAction}>
                    <ArrowLeft size={20} className="mr-2" />
                    {actionLabel}
                </Button>
            </div>
        </div>
    );
};

// Moldura simples dos passos de escolha (máquina/OP) — cabeçalho + ações.
const BenchCard = ({ title, subtitle, children, onBack, backLabel, onExit, exitLabel }) => (
    <div className="mx-auto max-w-3xl space-y-4">
        <div className="flex items-center justify-between gap-3">
            <div>
                <h2 className="text-2xl font-black text-black dark:text-white">{title}</h2>
                {subtitle ? <Mono className="text-sm text-mono-500">{subtitle}</Mono> : null}
            </div>
            <div className="flex gap-2">
                {onBack ? (
                    <Button variant="ghost" size="lg" onClick={onBack}>
                        <ArrowClockwise size={18} className="mr-2" />
                        {backLabel}
                    </Button>
                ) : null}
                {onExit ? (
                    <Button variant="ghost" size="lg" onClick={onExit}>
                        {exitLabel}
                    </Button>
                ) : null}
            </div>
        </div>
        {children}
    </div>
);

export default ProductionMlbBench;
